// lodash相关函数部分引入
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import xor from 'lodash/xor';
import {
    Command,
    IRoomSendMessageResult,
    IServerMessageBodyNoticeCursor,
    IServerMessageBodyNoticeUserNewInfo,
} from '@tencent/docs-connection';
import { IClientVarsService } from '../../../core/interfaces/client-vars-service.interface';
import { IUserManager } from '../core/interfaces/user-manager.interface';
import { CursorInfo, ICursorManager, IElementCursors } from '../core/interfaces/cursor-manager.interface';
import { ILogger } from '../../../core/slide-data-services.core';
import { IEventManager } from '@tencent/slide-sdk-model';
import { OnlineEditServices } from '../core/online-edit-services.core';
import { EditEvents, IEditEvents } from '../../../core/interfaces/event.interface';

const cursorColorList = [
    '#B30000',
    '#E6118F',
    '#F1633A',
    '#3646A0',
    '#3184E9',
    '#09A9B9',
    '#822F1D',
    '#C67600',
    '#FFB400',
    '#0D6C54',
    '#918B3D',
    '#7BB725',
];

export default class CursorManager implements ICursorManager {
    /**
     * 我的光标，用来做去重，如果两次通知的一样，就不再发cursor更新
     */
    private myCursor: CursorInfo;
    /**
     * 通知过来的光标
     */
    private cursorMap: {[userId: string]: CursorInfo} = {};

    private colorCandidateList: string[] = cursorColorList.slice();
    private colorMap: { [userId: string]: string } = {};

    private debouncedUpdateCursor = debounce(
        (cursor: CursorInfo) => {
            if (this.isSameCursorInfo(cursor, this.myCursor)) return;
            this.myCursor = cursor;
            this.sendCursorInfo(cursor);
        },
        100,
    );

    public constructor(
        private clientVarsService: IClientVarsService,
        public userManager: IUserManager,
        private event: IEditEvents,
        private sdkEventManager: IEventManager,
        private logger: ILogger,
    ) {
        this.sdkEventManager.socketEvents.onUserEnter(this.onUserEnter);
        this.sdkEventManager.socketEvents.onUserLeave(this.onUserLeave);
        this.sdkEventManager.socketEvents.onCursorMessage(this.onCursorMessage);
    }

    private get userId() {
        return this.clientVarsService.getClientVars().userId ?? '';
    }

    /**
     * 更新光标信息
     * @param info 光标信息
     */
    public updateCursor(cursor: CursorInfo) {
        // 这里节流一下，因为上层选择可能会短时间内多次触发
        this.debouncedUpdateCursor(cursor);
    }

    /**
     * 获取元素光标信息，key是元素id，value是用户id列表
     */
    public get elementCursors() {
        const elementCursors: IElementCursors = {};
        Object.keys(this.cursorMap).forEach((userId) => {
            if (userId === this.userId) return;
            this.cursorMap[userId].selection.forEach((selectedId) => {
                if (!elementCursors[selectedId]) {
                    elementCursors[selectedId] = [];
                }
                elementCursors[selectedId].push({
                    userId,
                    color: this.getColorForId(userId),
                });
            });
        });
        return elementCursors;
    }

    /**
     * 获取用户名称
     * @param userId 用户id，p.开头
     */
    public getUserName(userId: string): string | undefined {
        const uid = userId.replace('p.', '').replace('g.', '');
        const userInfo = this.userManager.getUserInfoFromCache(uid);
        return userInfo ? userInfo.markName : undefined;
    }

    /**
     * 用于重新加载光标信息
     */
    public reloadCursor() {
        this.event.emit(EditEvents.CursorInfoUpdate, { isNew: false });
    }

    /**
     * 用户进入
     * @param userInfo 用户信息
     */
    private onUserEnter = (userInfo: IServerMessageBodyNoticeUserNewInfo) => {
        const uid = userInfo.userId;
        if (!this.myCursor) return;
        this.sendMyCursorToUser(uid);
    };

    /**
     * 用户离开
     * @param userInfo 用户信息
     */
    private onUserLeave = (userInfo: IServerMessageBodyNoticeUserNewInfo) => {
        const uid = userInfo.userId;
        delete this.cursorMap[uid];
        this.event.emit(EditEvents.CursorInfoUpdate, { isNew: false });
    };

    /**
     * 处理push过来的光标信息
     * @param info 光标信息
     */
    private onCursorMessage = (info: IServerMessageBodyNoticeCursor
        & { targetUserId?: string; selection: string[] }) => {
        const { userId, targetUserId, selection } = info;
        this.logger.info(`[cursor] handle cursor info from ${userId}`);
        const cursor = { selection };
        // id不存在/自己是游客/发给自己的/定向发送的/info相同，不处理
        if (
            !userId
            || userId === this.userId
            || this.isGuest(this.userId)
            || (targetUserId && targetUserId !== this.userId)
            || this.isSameCursorInfo(this.cursorMap[userId], cursor)
        ) {
            return;
        }
        this.logger.info('[cursor] cursor changed');
        if (selection.length) {
            this.cursorMap[userId] = cursor;
        } else {
            delete this.cursorMap[userId];
        }
        // 有目标的就不是新的光标
        this.event.emit(EditEvents.CursorInfoUpdate, { isNew: !targetUserId });
    };

    /**
     * 发送光标信息
     * @param cursor 光标信息
     * @param toUserId 发给谁，undefined为发送给所有人
     * @param callback 回调
     */
    private async sendCursorInfo(
        cursor: CursorInfo,
        toUserId?: string,
    ) {
        const socket = OnlineEditServices.CollaborationManager()?.getSocket?.();
        // 未建立链接/自己或者对方是游客，不要发送
        if (!socket?.opened || this.isGuest(this.userId) || (toUserId && this.isGuest(toUserId))) {
            return;
        }
        // this.logger.info(`[cursor] send cursor for ${cursor.selection.length} element(s)`);
        socket.sendMessage({
            cmd: 'CURSOR_MESSAGE' as Command,
            taskId: 0,
            data: JSON.stringify({ userId: this.userId, ...cursor, targetUserId: toUserId }),
            onError: (_, __, ___, result: IRoomSendMessageResult) => {
                if (result?.errCode === 0 && result?.response?.onpost?.result === 0) {
                    this.logger.info('[cursor] send cursor info succeeded');
                    return;
                }
                const errorCode = result.errCode;
                const retCode = result.response?.onpost?.result;
                this.logger.info(`[cursor] send cursor info failed, reason: post error ${errorCode}, retCode ${retCode}`);
            },
        });
    }

    /**
     * 发送我的光标信息给特定用户
     * @param userId 用户Id
     * @param callback 回调
     */
    private sendMyCursorToUser(userId: string) {
        if (!this.myCursor) return;
        this.sendCursorInfo(this.myCursor, userId);
    }

    /**
     * 是否是相同的光标信息
     * @param cursorA 光标a
     * @param cursorB 光标b
     */
    private isSameCursorInfo(cursorA: CursorInfo, cursorB: CursorInfo): boolean {
        if (!cursorA && !cursorB) {
            return true;
        }
        if (!cursorA || !cursorB) {
            return false;
        }
        return isEmpty(xor(cursorA.selection, cursorB.selection));
    }

    /**
     * 取颜色，每次从颜色表随机取一个
     * @param {用户id} id
     */
    private getColorForId(id: string) {
        if (this.colorMap[id]) return this.colorMap[id];
        if (!this.colorCandidateList.length) this.colorCandidateList = cursorColorList.slice(0);
        const index = Math.floor(Math.random() * this.colorCandidateList.length);
        [this.colorMap[id]] = this.colorCandidateList.splice(index, 1);
        return this.colorMap[id];
    }

    /**
     * 是否是游客
     * @param userId 用户id
     */
    private isGuest(userId: string) {
        return userId?.startsWith('g.');
    }
}
